import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star, ShieldCheck, Crown } from 'lucide-react';
import { commerceAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Field, inputCls, Toggle, ConfirmDialog, fmtCve } from './shared';

const EMPTY = { plan_type: '', name_pt: '', name_en: '', name_fr: '', price_cve: 1500, total_credits: 1, featured_credits: 0, validity_days: 30, badge: '', label: '', description_pt: '', features_pt: '', sort_order: 10, active: true };

const PlanForm = ({ open, initial, onClose, onSaved, t }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial, badge: initial.badge || '', label: initial.label || '', name_en: initial.name_en || '', name_fr: initial.name_fr || '', features_pt: (initial.features_pt || []).join('\n') } : EMPTY);
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  const save = async () => {
    setSaving(true);
    const body = {
      ...f, badge: f.badge || null, label: f.label || null, name_en: f.name_en || null, name_fr: f.name_fr || null,
      features_pt: f.features_pt.split('\n').map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (isEdit) { const { plan_type, id, purchases_count, created_at, updated_at, ...patch } = body; await commerceAPI.updatePlan(initial.plan_type, patch); }
      else { if (!body.plan_type) delete body.plan_type; await commerceAPI.createPlan(body); }
      toast.success(t('adminCommerce.saved'));
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plan-form">
        <DialogHeader><DialogTitle>{isEdit ? t('adminCommerce.plans.edit') : t('adminCommerce.plans.new')}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('adminCommerce.plans.namePt')}><input className={inputCls} value={f.name_pt} onChange={set('name_pt')} data-testid="plan-name-pt" /></Field>
          {!isEdit && <Field label={t('adminCommerce.plans.slug')} hint={t('adminCommerce.plans.slugHint')}><input className={inputCls} value={f.plan_type} onChange={set('plan_type')} data-testid="plan-slug" /></Field>}
          <Field label="Nome EN"><input className={inputCls} value={f.name_en} onChange={set('name_en')} /></Field>
          <Field label="Nome FR"><input className={inputCls} value={f.name_fr} onChange={set('name_fr')} /></Field>
          <Field label={t('adminCommerce.plans.price')}><input type="number" min="0" className={inputCls} value={f.price_cve} onChange={set('price_cve')} disabled={initial?.plan_type === 'free'} data-testid="plan-price" /></Field>
          <Field label={t('adminCommerce.plans.credits')}><input type="number" min="1" className={inputCls} value={f.total_credits} onChange={set('total_credits')} data-testid="plan-credits" /></Field>
          <Field label={t('adminCommerce.plans.featured')}><input type="number" min="0" className={inputCls} value={f.featured_credits} onChange={set('featured_credits')} data-testid="plan-featured" /></Field>
          <Field label={t('adminCommerce.plans.validity')}><input type="number" min="1" className={inputCls} value={f.validity_days} onChange={set('validity_days')} data-testid="plan-validity" /></Field>
          <Field label={t('adminCommerce.plans.badge')}>
            <select className={inputCls} value={f.badge} onChange={set('badge')} data-testid="plan-badge">
              <option value="">—</option><option value="verified">Empresa Verificada</option><option value="premium">Empresa Premium</option>
            </select>
          </Field>
          <Field label={t('adminCommerce.plans.label')} hint={t('adminCommerce.plans.labelHint')}><input className={inputCls} value={f.label} onChange={set('label')} data-testid="plan-label" /></Field>
          <Field label={t('adminCommerce.plans.order')}><input type="number" min="0" className={inputCls} value={f.sort_order} onChange={set('sort_order')} /></Field>
          <div className="flex items-end"><Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={f.active ? t('adminCommerce.active') : t('adminCommerce.inactive')} testId="plan-active-toggle" /></div>
          <Field label={t('adminCommerce.plans.description')} className="sm:col-span-2"><textarea className={`${inputCls} h-20`} value={f.description_pt} onChange={set('description_pt')} /></Field>
          <Field label={t('adminCommerce.plans.features')} hint={t('adminCommerce.plans.featuresHint')} className="sm:col-span-2"><textarea className={`${inputCls} h-28`} value={f.features_pt} onChange={set('features_pt')} data-testid="plan-features" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('adminCommerce.cancel')}</Button>
          <Button onClick={save} disabled={saving || !f.name_pt} className="bg-emerald-600 hover:bg-emerald-700" data-testid="plan-save-btn">{t('adminCommerce.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BADGE_ICON = { verified: ShieldCheck, premium: Crown };

const PlanRow = ({ p, onEdit, onToggle, onDelete, t }) => {
  const BadgeIcon = BADGE_ICON[p.badge];
  return (
    <Card className={`p-4 flex flex-col md:flex-row md:items-center gap-4 ${p.active ? '' : 'opacity-60'}`} data-testid={`plan-row-${p.plan_type}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-slate-900">{p.name_pt}</h3>
          <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{p.plan_type}</code>
          {p.label && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">{p.label}</span>}
          {BadgeIcon && <BadgeIcon className="h-4 w-4 text-amber-600" />}
          {!p.active && <span className="text-[11px] font-bold uppercase text-rose-700">{t('adminCommerce.inactive')}</span>}
        </div>
        <p className="text-sm text-slate-600 mt-1">
          <strong>{fmtCve(p.price_cve)}</strong> · {p.total_credits} {t('adminCommerce.plans.creditsShort')}
          {p.featured_credits > 0 && <> · <Star className="inline h-3.5 w-3.5 text-amber-500" /> {p.featured_credits}</>}
          {' '}· {p.validity_days} {t('adminCommerce.days')} · {t('adminCommerce.plans.sold', { count: p.purchases_count || 0 })}
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`plan-edit-${p.plan_type}`}><Pencil className="h-4 w-4 mr-1" />{t('adminCommerce.edit')}</Button>
        <Button size="sm" variant="outline" onClick={() => onToggle(p)} data-testid={`plan-toggle-${p.plan_type}`}>{p.active ? t('adminCommerce.deactivate') : t('adminCommerce.activate')}</Button>
        {p.plan_type !== 'free' && <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(p)} data-testid={`plan-delete-${p.plan_type}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </Card>
  );
};

export const PlansTab = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const load = () => commerceAPI.listPlans().then((r) => setPlans(r.data)).catch(() => toast.error(t('adminCommerce.loadError')));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (p) => { await commerceAPI.updatePlan(p.plan_type, { active: !p.active }); load(); };
  const del = async () => {
    try {
      const { data } = await commerceAPI.deletePlan(toDelete.plan_type);
      toast.success(data.deleted ? t('adminCommerce.plans.deleted') : t('adminCommerce.plans.deactivatedInstead'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    setToDelete(null); load();
  };
  return (
    <div data-testid="plans-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-xl">{t('adminCommerce.plans.intro')}</p>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="plan-new-btn"><Plus className="h-4 w-4 mr-1" />{t('adminCommerce.plans.new')}</Button>
      </div>
      <div className="space-y-3">
        {plans.map((p) => <PlanRow key={p.plan_type} p={p} t={t} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} onDelete={setToDelete} />)}
      </div>
      <PlanForm open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} t={t} />
      <ConfirmDialog open={Boolean(toDelete)} title={t('adminCommerce.plans.deleteTitle')} body={t('adminCommerce.plans.deleteBody', { name: toDelete?.name_pt })} onCancel={() => setToDelete(null)} onConfirm={del} confirmLabel={t('adminCommerce.delete')} cancelLabel={t('adminCommerce.cancel')} testId="plan-delete-dialog" />
    </div>
  );
};
